import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import * as React from 'react';
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IDsiResult } from '../../../data/providers/reports/dsiReportProvider';
import { ISize } from '../../../data/types';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { Card } from '../../shared/card/Card';
import { GraphContainer } from '../../shared/graphs/GraphContainer';
import { legendFormatter } from '../../shared/graphs/legendFormatter';
import { EPdfMode, isPdf, shouldInvert } from '../../shared/pdf/Pdf';
import { colors } from '../colors';
import { DSITooltip } from './DSITooltip';

interface IProps {
  results: IDsiResult[];
  pdfMode: EPdfMode;
}

interface IDataPoint {
  [metricKey: string]: number;
  Date: number;
}

interface IGraphData {
  labels: string[];
  data: IDataPoint[];
}

export const DsiGraph = (props: IProps): React.ReactElement => {
  const data = React.useMemo((): IGraphData => {
    const labels: string[] = ['CMJ Peak Force', 'Iso Peak Force'];

    const mData = props.results.map((result: IDsiResult): IDataPoint => {
      return {
        Date: result.timestamp,
        ['CMJ Peak Force']: result.cmjPeak,
        ['Iso Peak Force']: result.isoPeak,
        DSI: result.dsi,
      };
    });

    const sortedData = sortBy(mData, 'Date', 'asc');

    return {
      labels,
      data: sortedData,
    };
  }, [JSON.stringify(props.results)]);

  const [maxForce, maxDSI] = React.useMemo((): number[] => {
    return props.results.reduce(
      (acc: number[], curr: IDsiResult): number[] => {
        const highForceValue =
          curr.cmjPeak > curr.isoPeak ? curr.cmjPeak : curr.isoPeak;
        const force = acc[0] > highForceValue ? acc[0] : highForceValue;
        const dsi = acc[1] > curr.dsi ? acc[1] : curr.dsi;
        return [force, dsi];
      },
      [0, 0]
    );
  }, [JSON.stringify(props.results)]);

  return (
    <Row>
      <Card
        noTopRadius
        inverse={shouldInvert(props.pdfMode)}
        style={{
          padding: '0px',
          minHeight: 'var(--graph-min-height)',
          ...(isPdf(props.pdfMode) ? { overflow: 'visible', height: 'auto' } : {}),
        }}
      >
        <Column>
          <GraphContainer
            inverse={shouldInvert(props.pdfMode)}
            style={{ paddingBlockEnd: '0px', ...(isPdf(props.pdfMode) ? { height: 'auto' } : {}) }}
          >
            <AutoSizer>
              {(size: ISize): React.ReactElement => {
                return (
                  <ComposedChart
                    width={size.width}
                    height={size.height}
                    data={data.data}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid
                      vertical={false}
                      horizontal
                      stroke='#ccc'
                      strokeOpacity={0.25}
                    />

                    <XAxis
                      dataKey='Date'
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'var(--color-text)' }}
                      tickFormatter={tickFormatter}
                    />

                    <YAxis
                      orientation='right'
                      yAxisId='right'
                      domain={[0, maxForce]}
                      axisLine={false}
                      tickLine={false}
                      tickCount={10}
                      tick={{ fill: 'var(--color-text)' }}
                    />

                    <YAxis
                      yAxisId='left'
                      orientation='left'
                      dataKey={'DSI'}
                      domain={[0, 2]}
                      tickCount={10}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'var(--color-text)' }}
                    />

                    <Tooltip
                      cursor={false}
                      content={DSITooltip}
                      formatter={tickFormatter}
                    />

                    {data.labels.map(
                      (m: string, i: number): React.ReactElement => {
                        return (
                          <Bar
                            key={i}
                            dataKey={m}
                            fill={colors[i]}
                            yAxisId='right'
                          />
                        );
                      }
                    )}

                    <Line
                      dataKey={'DSI'}
                      yAxisId='left'
                      connectNulls
                      stroke={
                        props.pdfMode === EPdfMode.PRINT
                          ? 'var(--color-dark-text)'
                          : 'var(--color-text)'
                      }
                      strokeWidth={2}
                      color='var(--color-scatter-primary)'
                      dot={{
                        stroke: 'rgba(0,0,0, .85)',
                        strokeWidth: 2,
                        r: 6,
                        strokeDasharray: '',
                      }}
                      fill={
                        props.pdfMode === EPdfMode.PRINT
                          ? 'var(--color-scatter-primary-inverse)'
                          : 'var(--color-scatter-primary)'
                      }
                    />

                    <Legend
                      iconType='circle'
                      formatter={(value) =>
                        legendFormatter(value, props.pdfMode)
                      }
                    />
                  </ComposedChart>
                );
              }}
            </AutoSizer>
          </GraphContainer>
        </Column>
      </Card>
    </Row>
  );
};
const tickFormatter = (tick: number): string =>
  tick > 12875256000 ? dayjs.utc(Number(tick)).format('M/D') : tick.toString();
